import React, { useState } from 'react';
import { UtensilsCrossed, CheckCircle, XCircle, RefreshCw, Download, Moon, Clock, Zap } from 'lucide-react';
import { reportingAPI } from '../../services/api';

const fmtDate = (d) => `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;

const exportCsv = (rows, columns, filename, year, month) => {
  const transdate = fmtDate(new Date(year, month - 1, 20));
  const postdate  = fmtDate(new Date(year, month - 1, 21));
  const prefixCols = [
    { key: '__transdate', label: 'transdate' },
    { key: '__postdate',  label: 'postdate' },
  ];
  const allColumns = [...prefixCols, ...columns];
  const enriched = rows.map(r => ({ ...r, __transdate: transdate, __postdate: postdate }));
  const header = allColumns.map(c => c.label).join(',');
  const body   = enriched.map(r =>
    allColumns.map(c => {
      const val = r[c.key] ?? '';
      return String(val).includes(',') ? `"${val}"` : val;
    }).join(',')
  );
  const csv  = [header, ...body].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const TABS = [
  { id: 'attendance',  label: 'Attendance Allowance', icon: CheckCircle },
  { id: 'meal',        label: 'Meal Allowance',        icon: UtensilsCrossed },
  { id: 'shift',       label: 'Shift Allowance',       icon: Moon },
  { id: 'lateness',    label: 'Lateness',              icon: Clock },
  { id: 'overtime',    label: 'Overtime',              icon: Zap },
  { id: 'ot-meal',     label: 'OT Meal Allowance',     icon: UtensilsCrossed },
];

const today = new Date();

// ---------------------------------------------------------------------------
// Shared: month/year picker + generate button
// ---------------------------------------------------------------------------
const CyclePicker = ({ year, month, onChange, onGenerate, loading }) => {
  const months = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
  ];
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <select
        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        value={month}
        onChange={e => onChange(year, parseInt(e.target.value))}
      >
        {months.map((m, i) => (
          <option key={i + 1} value={i + 1}>{m}</option>
        ))}
      </select>
      <select
        className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        value={year}
        onChange={e => onChange(parseInt(e.target.value), month)}
      >
        {[today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1].map(y => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
      <button
        onClick={onGenerate}
        disabled={loading}
        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
      >
        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        {loading ? 'Generating…' : 'Generate'}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared: summary bar
// ---------------------------------------------------------------------------
const SummaryBar = ({ rows, eligibleKey }) => {
  const total    = rows.length;
  const eligible = rows.filter(r => r[eligibleKey]).length;
  const pct      = total ? Math.round((eligible / total) * 100) : 0;
  return (
    <div className="grid grid-cols-3 gap-4 mb-4">
      {[
        { label: 'Total Users',  value: total,             color: 'text-gray-700' },
        { label: 'Eligible',     value: eligible,           color: 'text-green-600' },
        { label: 'Not Eligible', value: total - eligible,   color: 'text-red-500' },
      ].map(({ label, value, color }) => (
        <div key={label} className="bg-white rounded-lg shadow p-4 text-center">
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          <p className="text-xs text-gray-500 mt-1">{label}</p>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Attendance Allowance tab
// ---------------------------------------------------------------------------
const AttendanceAllowanceTab = () => {
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [generated, setGenerated] = useState(false);
  const [filter, setFilter]   = useState('all'); // all | eligible | not_eligible

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportingAPI.getAttendanceAllowance(year, month);
      setRows(res.data.report || []);
      setGenerated(true);
    } catch (e) {
      setError(e.response?.data?.details || 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  };

  const filtered = rows.filter(r => {
    if (filter === 'eligible')     return r.is_eligible;
    if (filter === 'not_eligible') return !r.is_eligible;
    return true;
  });

  const cycleLabel = () => {
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const prevM  = month === 1 ? 12 : month - 1;
    const prevY  = month === 1 ? year - 1 : year;
    return `21 ${names[prevM - 1]} ${prevY} — 20 ${names[month - 1]} ${year}`;
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-gray-800">Attendance Allowance</h3>
          {generated && <p className="text-xs text-gray-400 mt-0.5">Cycle: {cycleLabel()}</p>}
        </div>
        <CyclePicker
          year={year} month={month}
          onChange={(y, m) => { setYear(y); setMonth(m); }}
          onGenerate={generate}
          loading={loading}
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      )}

      {generated && rows.length > 0 && (
        <>
          <SummaryBar rows={rows} eligibleKey="is_eligible" />

          {/* Export + filter row */}
          <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex gap-2">
            {[
              { id: 'all',         label: `All (${rows.length})` },
              { id: 'eligible',    label: `Eligible (${rows.filter(r => r.is_eligible).length})` },
              { id: 'not_eligible',label: `Not Eligible (${rows.filter(r => !r.is_eligible).length})` },
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  filter === f.id
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => exportCsv(
              rows.filter(r => r.is_eligible),
              [
                { key: 'staff_no',       label: 'Staff No' },
                { key: 'name',           label: 'Name' },
                { key: 'department',     label: 'Department' },
                { key: 'eligible_days',  label: 'Eligible Days' },
                { key: 'total_workdays', label: 'Total Workdays' },
                { key: 'code',           label: 'Code' },
              ],
              `attendance_allowance_${year}_${String(month).padStart(2,'0')}.csv`,
              year, month
            )}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
          >
            <Download className="h-4 w-4" /> Export CSV
          </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Staff No', 'Name', 'Department', 'Eligible / Total', 'Status', 'Code'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map(r => (
                  <tr key={r.staff_no} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-gray-700">{r.staff_no}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                    <td className="px-4 py-3 text-gray-500">{r.department}</td>
                    <td className="px-4 py-3 text-gray-700">
                      <span className={r.is_eligible ? 'text-green-600 font-medium' : 'text-red-500 font-medium'}>
                        {r.eligible_days}
                      </span>
                      <span className="text-gray-400"> / {r.total_workdays}</span>
                    </td>
                    <td className="px-4 py-3">
                      {r.is_eligible ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle className="h-3 w-3" /> Eligible
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600">
                          <XCircle className="h-3 w-3" /> Not Eligible
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-blue-600">{r.code || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <div className="px-4 py-10 text-center text-gray-400 text-sm">No records match the current filter.</div>
            )}
          </div>
        </>
      )}

      {generated && rows.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow px-4 py-10 text-center text-gray-400 text-sm">
          No records found for this cycle.
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Meal Allowance tab
// ---------------------------------------------------------------------------
const MealAllowanceTab = () => {
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [rows, setRows]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportingAPI.getMealAllowance(year, month);
      setRows(res.data.report || []);
      setGenerated(true);
    } catch (e) {
      setError(e.response?.data?.details || 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  };

  const cycleLabel = () => {
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const prevM  = month === 1 ? 12 : month - 1;
    const prevY  = month === 1 ? year - 1 : year;
    return `21 ${names[prevM - 1]} ${prevY} — 20 ${names[month - 1]} ${year}`;
  };

  const totalEligibleDays = rows.reduce((sum, r) => sum + r.eligible_days, 0);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-gray-800">Meal Allowance</h3>
          {generated && <p className="text-xs text-gray-400 mt-0.5">Cycle: {cycleLabel()}</p>}
        </div>
        <CyclePicker
          year={year} month={month}
          onChange={(y, m) => { setYear(y); setMonth(m); }}
          onGenerate={generate}
          loading={loading}
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      )}

      {generated && rows.length > 0 && (
        <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Users Evaluated',    value: rows.length,        color: 'text-gray-700' },
              { label: 'Total Eligible Days', value: totalEligibleDays,  color: 'text-green-600' },
              { label: 'Working Days / User', value: rows[0]?.total_workdays ?? '—', color: 'text-blue-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-lg shadow p-4 text-center">
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => exportCsv(
                rows,
                [
                  { key: 'staff_no',       label: 'Staff No' },
                  { key: 'name',           label: 'Name' },
                  { key: 'department',     label: 'Department' },
                  { key: 'eligible_days',  label: 'Eligible Days' },
                  { key: 'total_workdays', label: 'Total Workdays' },
                  { key: 'code',           label: 'Code' },
                ],
                `meal_allowance_${year}_${String(month).padStart(2,'0')}.csv`,
                year, month
              )}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>

          <div className="bg-white rounded-lg shadow overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {['Staff No', 'Name', 'Department', 'Eligible Days', 'Total Workdays', 'Code'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => (
                  <tr key={r.staff_no} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-gray-700">{r.staff_no}</td>
                    <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                    <td className="px-4 py-3 text-gray-500">{r.department}</td>
                    <td className="px-4 py-3">
                      <span className="text-green-600 font-semibold">{r.eligible_days}</span>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{r.total_workdays}</td>
                    <td className="px-4 py-3 font-mono text-blue-600">{r.code || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {generated && rows.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow px-4 py-10 text-center text-gray-400 text-sm">
          No records found for this cycle.
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shift Allowance tab
// ---------------------------------------------------------------------------
const SA_COLS_DETAIL = [
  { key: 'staff_no',   label: 'Staff No' },
  { key: 'name',       label: 'Name' },
  { key: 'department', label: 'Department' },
  { key: 'workday',    label: 'Date' },
  { key: 'work_hours', label: 'Work Hours' },
  { key: 'code',       label: 'Code' },
];
const SA_COLS_SUMMARY = [
  { key: 'staff_no',   label: 'Staff No' },
  { key: 'name',       label: 'Name' },
  { key: 'department', label: 'Department' },
  { key: 'code',       label: 'Code' },
  { key: 'count',      label: 'Count' },
];

const CODE_COLOURS = {
  SA01: 'bg-blue-100 text-blue-700',
  SA02: 'bg-indigo-100 text-indigo-700',
  SA03: 'bg-orange-100 text-orange-700',
  SA04: 'bg-purple-100 text-purple-700',
  SA05: 'bg-teal-100 text-teal-700',
  SA06: 'bg-cyan-100 text-cyan-700',
  SA07: 'bg-amber-100 text-amber-700',
  SA08: 'bg-rose-100 text-rose-700',
};

const ShiftAllowanceTab = () => {
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [detail, setDetail]   = useState([]);
  const [summary, setSummary] = useState([]);
  const [view, setView]       = useState('summary'); // 'summary' | 'detail'
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportingAPI.getShiftAllowance(year, month);
      setDetail(res.data.detail   || []);
      setSummary(res.data.summary || []);
      setGenerated(true);
    } catch (e) {
      setError(e.response?.data?.details || 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  };

  const cycleLabel = () => {
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const prevM = month === 1 ? 12 : month - 1;
    const prevY = month === 1 ? year - 1 : year;
    return `21 ${names[prevM - 1]} ${prevY} — 20 ${names[month - 1]} ${year}`;
  };

  const uniqueUsers = new Set(summary.map(r => r.staff_no)).size;
  const filename    = `shift_allowance_${year}_${String(month).padStart(2, '0')}`;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-gray-800">Shift Allowance (TS00)</h3>
          {generated && <p className="text-xs text-gray-400 mt-0.5">Cycle: {cycleLabel()}</p>}
        </div>
        <CyclePicker
          year={year} month={month}
          onChange={(y, m) => { setYear(y); setMonth(m); }}
          onGenerate={generate}
          loading={loading}
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      )}

      {generated && (detail.length > 0 || summary.length > 0) && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Users',          value: uniqueUsers,    color: 'text-gray-700' },
              { label: 'Worked Records', value: detail.length,  color: 'text-blue-600' },
              { label: 'Summary Rows',   value: summary.length, color: 'text-indigo-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-lg shadow p-4 text-center">
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>

          {/* View toggle + export */}
          <div className="flex items-center justify-between">
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              {[['summary', 'Summary'], ['detail', 'Detail']].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setView(id)}
                  className={`px-4 py-1.5 ${view === id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={() => exportCsv(
                view === 'summary' ? summary : detail,
                view === 'summary' ? SA_COLS_SUMMARY : SA_COLS_DETAIL,
                `${filename}_${view}.csv`,
                year, month
              )}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>

          {/* Summary table */}
          {view === 'summary' && (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Staff No', 'Name', 'Department', 'Code', 'Count'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {summary.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-gray-700">{r.staff_no}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                      <td className="px-4 py-3 text-gray-500">{r.department}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CODE_COLOURS[r.code] || 'bg-gray-100 text-gray-600'}`}>
                          {r.code}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Detail table */}
          {view === 'detail' && (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Staff No', 'Name', 'Department', 'Date', 'Work Hours', 'Code'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detail.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-gray-700">{r.staff_no}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                      <td className="px-4 py-3 text-gray-500">{r.department}</td>
                      <td className="px-4 py-3 text-gray-700">{r.workday}</td>
                      <td className="px-4 py-3 text-gray-700">{r.work_hours}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CODE_COLOURS[r.code] || 'bg-gray-100 text-gray-600'}`}>
                          {r.code}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {generated && detail.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow px-4 py-10 text-center text-gray-400 text-sm">
          No shift allowance records found for this cycle.
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overtime tab
// ---------------------------------------------------------------------------
const OT_COLS_DETAIL = [
  { key: 'staff_no',   label: 'Staff No' },
  { key: 'name',       label: 'Name' },
  { key: 'department', label: 'Department' },
  { key: 'date',       label: 'Date' },
  { key: 'day_type',   label: 'Day Type' },
  { key: 'ot_hours',   label: 'OT Hours' },
  { key: 'code',       label: 'Code' },
];
const OT_COLS_SUMMARY = [
  { key: 'staff_no',       label: 'Staff No' },
  { key: 'name',           label: 'Name' },
  { key: 'department',     label: 'Department' },
  { key: 'code',           label: 'Code' },
  { key: 'total_ot_hours', label: 'Total OT Hours' },
  { key: 'count',          label: 'Sessions' },
];

const OT_CODE_COLOURS = {
  'Below4001_WD1.5':      'bg-blue-100 text-blue-700',
  'Exceed4000_WD1.5':     'bg-blue-100 text-blue-800',
  'Below4001_SUN_2.0':    'bg-indigo-100 text-indigo-700',
  'Exceed4000_SUN.2.0':   'bg-indigo-100 text-indigo-800',
  'PH_Below4001WHx2.0':   'bg-orange-100 text-orange-700',
  'PH_Exceed4001_WH2.0':  'bg-orange-100 text-orange-800',
  'PH_Below4001_3.0':     'bg-red-100 text-red-700',
  'PH_Exceed4001_AFT8H':  'bg-red-100 text-red-800',
  'FLAT_NonOperation':    'bg-purple-100 text-purple-700',
};

const OT_DAY_TYPE_COLOURS = {
  'Weekday':        'text-gray-600',
  'Saturday':       'text-blue-600',
  'Sunday':         'text-indigo-600',
  'Public Holiday': 'text-orange-600',
};

const OvertimeTab = () => {
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [detail, setDetail]   = useState([]);
  const [summary, setSummary] = useState([]);
  const [view, setView]       = useState('summary');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportingAPI.getOvertimeReport(year, month);
      setDetail(res.data.detail   || []);
      setSummary(res.data.summary || []);
      setGenerated(true);
    } catch (e) {
      setError(e.response?.data?.details || 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  };

  const cycleLabel = () => {
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const prevM = month === 1 ? 12 : month - 1;
    const prevY = month === 1 ? year - 1 : year;
    return `21 ${names[prevM - 1]} ${prevY} — 20 ${names[month - 1]} ${year}`;
  };

  const uniqueUsers    = new Set(detail.map(r => r.staff_no)).size;
  const totalOtHours   = detail.reduce((s, r) => s + (r.ot_hours || 0), 0);
  const filename       = `overtime_${year}_${String(month).padStart(2, '0')}`;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-gray-800">Overtime</h3>
          {generated && <p className="text-xs text-gray-400 mt-0.5">Cycle: {cycleLabel()}</p>}
        </div>
        <CyclePicker
          year={year} month={month}
          onChange={(y, m) => { setYear(y); setMonth(m); }}
          onGenerate={generate}
          loading={loading}
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      )}

      {generated && (detail.length > 0 || summary.length > 0) && (
        <>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Users with OT',   value: uniqueUsers,              color: 'text-gray-700' },
              { label: 'OT Sessions',     value: detail.length,            color: 'text-blue-600' },
              { label: 'Total OT Hours',  value: totalOtHours.toFixed(2),  color: 'text-indigo-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-lg shadow p-4 text-center">
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              {[['summary', 'Summary'], ['detail', 'Detail']].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setView(id)}
                  className={`px-4 py-1.5 ${view === id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={() => exportCsv(
                view === 'summary' ? summary : detail,
                view === 'summary' ? OT_COLS_SUMMARY : OT_COLS_DETAIL,
                `${filename}_${view}.csv`,
                year, month
              )}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>

          {view === 'summary' && (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Staff No', 'Name', 'Department', 'Code', 'Total OT Hours', 'Sessions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {summary.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-gray-700">{r.staff_no}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                      <td className="px-4 py-3 text-gray-500">{r.department}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${OT_CODE_COLOURS[r.code] || 'bg-gray-100 text-gray-600'}`}>
                          {r.code}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800">{r.total_ot_hours.toFixed(2)}</td>
                      <td className="px-4 py-3 text-gray-600">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {view === 'detail' && (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Staff No', 'Name', 'Department', 'Date', 'Day Type', 'OT Hours', 'Code'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detail.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-gray-700">{r.staff_no}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                      <td className="px-4 py-3 text-gray-500">{r.department}</td>
                      <td className="px-4 py-3 text-gray-700">{r.date}</td>
                      <td className={`px-4 py-3 font-medium ${OT_DAY_TYPE_COLOURS[r.day_type] || 'text-gray-600'}`}>
                        {r.day_type}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800">{r.ot_hours.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${OT_CODE_COLOURS[r.code] || 'bg-gray-100 text-gray-600'}`}>
                          {r.code}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {generated && detail.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow px-4 py-10 text-center text-gray-400 text-sm">
          No approved overtime records found for this cycle.
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Lateness tab
// ---------------------------------------------------------------------------
const LATE_COLS_DETAIL = [
  { key: 'staff_no',     label: 'Staff No' },
  { key: 'name',         label: 'Name' },
  { key: 'department',   label: 'Department' },
  { key: 'workday',      label: 'Date' },
  { key: 'clock_in',     label: 'Clock In' },
  { key: 'clock_out',    label: 'Clock Out' },
  { key: 'reason',       label: 'Reason' },
  { key: 'late_minutes', label: 'Late Minutes' },
  { key: 'code',         label: 'Code' },
];
const LATE_COLS_SUMMARY = [
  { key: 'staff_no',           label: 'Staff No' },
  { key: 'name',               label: 'Name' },
  { key: 'department',         label: 'Department' },
  { key: 'ul_days',            label: 'UL Days' },
  { key: 'late_in_days',       label: 'Late In Days' },
  { key: 'early_out_days',     label: 'Early Out Days' },
  { key: 'total_late_minutes', label: 'Total Late Minutes' },
  { key: 'code',               label: 'Code' },
];

const CODE_LATE_COLOURS = {
  LATE: 'bg-red-100 text-red-700',
  UL:   'bg-orange-100 text-orange-700',
};

const REASON_COLOURS = {
  'Late':             'bg-red-50 text-red-700',
  'Early Out':        'bg-amber-50 text-amber-700',
  'Late & Early Out': 'bg-purple-50 text-purple-700',
};

const LatenessTab = () => {
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [detail, setDetail]   = useState([]);
  const [summary, setSummary] = useState([]);
  const [view, setView]       = useState('summary');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportingAPI.getLateness(year, month);
      setDetail(res.data.detail   || []);
      setSummary(res.data.summary || []);
      setGenerated(true);
    } catch (e) {
      setError(e.response?.data?.details || 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  };

  const cycleLabel = () => {
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const prevM = month === 1 ? 12 : month - 1;
    const prevY = month === 1 ? year - 1 : year;
    return `21 ${names[prevM - 1]} ${prevY} — 20 ${names[month - 1]} ${year}`;
  };

  const uniqueUsers      = new Set(detail.map(r => r.staff_no)).size;
  const totalLateMinutes = detail.reduce((s, r) => s + (r.late_minutes || 0), 0);
  const filename         = `lateness_${year}_${String(month).padStart(2, '0')}`;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-gray-800">Lateness / Deduction</h3>
          {generated && <p className="text-xs text-gray-400 mt-0.5">Cycle: {cycleLabel()}</p>}
        </div>
        <CyclePicker
          year={year} month={month}
          onChange={(y, m) => { setYear(y); setMonth(m); }}
          onGenerate={generate}
          loading={loading}
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      )}

      {generated && (detail.length > 0 || summary.length > 0) && (
        <>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Users Affected',      value: uniqueUsers,      color: 'text-gray-700' },
              { label: 'Late Incidents',       value: detail.length,    color: 'text-red-600' },
              { label: 'Total Late Minutes',   value: totalLateMinutes, color: 'text-orange-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-lg shadow p-4 text-center">
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              {[['summary', 'Summary'], ['detail', 'Detail']].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setView(id)}
                  className={`px-4 py-1.5 ${view === id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={() => exportCsv(
                view === 'summary' ? summary : detail,
                view === 'summary' ? LATE_COLS_SUMMARY : LATE_COLS_DETAIL,
                `${filename}_${view}.csv`,
                year, month
              )}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>

          {view === 'summary' && (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Staff No', 'Name', 'Department', 'UL Days', 'Late In', 'Early Out', 'Total Late Mins', 'Code'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {summary.map(r => (
                    <tr key={r.staff_no} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-gray-700">{r.staff_no}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                      <td className="px-4 py-3 text-gray-500">{r.department}</td>
                      <td className="px-4 py-3">
                        <span className="text-orange-600 font-semibold">{r.ul_days}</span>
                      </td>
                      <td className="px-4 py-3 text-red-600 font-semibold">{r.late_in_days ?? 0}</td>
                      <td className="px-4 py-3 text-amber-600 font-semibold">{r.early_out_days ?? 0}</td>
                      <td className="px-4 py-3 font-semibold text-gray-800">{r.total_late_minutes}</td>
                      <td className="px-4 py-3">
                        {r.code && r.code.split(', ').map(c => (
                          <span key={c} className={`inline-block mr-1 px-2 py-0.5 rounded-full text-xs font-medium ${CODE_LATE_COLOURS[c] || 'bg-gray-100 text-gray-600'}`}>{c}</span>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {view === 'detail' && (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Staff No', 'Name', 'Department', 'Date', 'Clock In', 'Clock Out', 'Reason', 'Late Mins', 'Code'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detail.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-gray-700">{r.staff_no}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                      <td className="px-4 py-3 text-gray-500">{r.department}</td>
                      <td className="px-4 py-3 text-gray-700">{r.workday}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {r.clock_in ? r.clock_in.substring(11, 16) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {r.clock_out ? r.clock_out.substring(11, 16) : <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {r.reason && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${REASON_COLOURS[r.reason] || 'bg-gray-100 text-gray-600'}`}>
                            {r.reason}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800">{r.late_minutes || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${CODE_LATE_COLOURS[r.code] || 'bg-gray-100 text-gray-600'}`}>
                          {r.code}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {generated && detail.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow px-4 py-10 text-center text-gray-400 text-sm">
          No lateness records found for this cycle.
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// OT Meal Allowance tab
// ---------------------------------------------------------------------------
const OT_MEAL_COLS_DETAIL = [
  { key: 'staff_no',   label: 'Staff No' },
  { key: 'name',       label: 'Name' },
  { key: 'department', label: 'Department' },
  { key: 'date',       label: 'Date' },
  { key: 'day_type',   label: 'Day Type' },
  { key: 'ot_hours',   label: 'OT Hours' },
  { key: 'code',       label: 'Code' },
];
const OT_MEAL_COLS_SUMMARY = [
  { key: 'staff_no',   label: 'Staff No' },
  { key: 'name',       label: 'Name' },
  { key: 'department', label: 'Department' },
  { key: 'code',       label: 'Code' },
  { key: 'count',      label: 'Sessions' },
];

const OT_MEAL_CODE_COLOURS = {
  OT01: 'bg-orange-100 text-orange-700',
  OT02: 'bg-blue-100 text-blue-700',
};

const OTMealAllowanceTab = () => {
  const [year, setYear]   = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [detail, setDetail]   = useState([]);
  const [summary, setSummary] = useState([]);
  const [view, setView]       = useState('summary');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  const [generated, setGenerated] = useState(false);

  const generate = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportingAPI.getOvertimeMealAllowance(year, month);
      setDetail(res.data.detail   || []);
      setSummary(res.data.summary || []);
      setGenerated(true);
    } catch (e) {
      setError(e.response?.data?.details || 'Failed to generate report.');
    } finally {
      setLoading(false);
    }
  };

  const cycleLabel = () => {
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const prevM = month === 1 ? 12 : month - 1;
    const prevY = month === 1 ? year - 1 : year;
    return `21 ${names[prevM - 1]} ${prevY} — 20 ${names[month - 1]} ${year}`;
  };

  const uniqueUsers = new Set(detail.map(r => r.staff_no)).size;
  const filename    = `ot_meal_allowance_${year}_${String(month).padStart(2, '0')}`;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow p-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-gray-800">OT Meal Allowance</h3>
          {generated && <p className="text-xs text-gray-400 mt-0.5">Cycle: {cycleLabel()}</p>}
        </div>
        <CyclePicker
          year={year} month={month}
          onChange={(y, m) => { setYear(y); setMonth(m); }}
          onGenerate={generate}
          loading={loading}
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>
      )}

      {generated && (detail.length > 0 || summary.length > 0) && (
        <>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Users Eligible', value: uniqueUsers,   color: 'text-gray-700' },
              { label: 'OT01 Sessions',  value: detail.filter(r => r.code === 'OT01').length, color: 'text-orange-600' },
              { label: 'OT02 Sessions',  value: detail.filter(r => r.code === 'OT02').length, color: 'text-blue-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-white rounded-lg shadow p-4 text-center">
                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-gray-500 mt-1">{label}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              {[['summary', 'Summary'], ['detail', 'Detail']].map(([id, label]) => (
                <button
                  key={id}
                  onClick={() => setView(id)}
                  className={`px-4 py-1.5 ${view === id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <button
              onClick={() => exportCsv(
                view === 'summary' ? summary : detail,
                view === 'summary' ? OT_MEAL_COLS_SUMMARY : OT_MEAL_COLS_DETAIL,
                `${filename}_${view}.csv`,
                year, month
              )}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>

          {view === 'summary' && (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Staff No', 'Name', 'Department', 'Code', 'Sessions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {summary.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-gray-700">{r.staff_no}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                      <td className="px-4 py-3 text-gray-500">{r.department}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${OT_MEAL_CODE_COLOURS[r.code] || 'bg-gray-100 text-gray-600'}`}>
                          {r.code}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {view === 'detail' && (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {['Staff No', 'Name', 'Department', 'Date', 'Day Type', 'OT Hours', 'Code'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {detail.map((r, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-gray-700">{r.staff_no}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{r.name}</td>
                      <td className="px-4 py-3 text-gray-500">{r.department}</td>
                      <td className="px-4 py-3 text-gray-700">{r.date}</td>
                      <td className={`px-4 py-3 font-medium ${OT_DAY_TYPE_COLOURS[r.day_type] || 'text-gray-600'}`}>
                        {r.day_type}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800">{r.ot_hours.toFixed(2)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${OT_MEAL_CODE_COLOURS[r.code] || 'bg-gray-100 text-gray-600'}`}>
                          {r.code}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {generated && detail.length === 0 && !loading && (
        <div className="bg-white rounded-lg shadow px-4 py-10 text-center text-gray-400 text-sm">
          No OT meal allowance records found for this cycle.
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Panel shell
// ---------------------------------------------------------------------------
const ReportingPanel = () => {
  const [activeTab, setActiveTab] = useState('attendance');

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold text-gray-900">Reporting</h1>
      </div>

      <div className="border-b border-gray-200 bg-white shadow-sm rounded-t-lg px-4 pt-2">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`${
                activeTab === id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <Icon className="h-5 w-5 mr-2" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        {activeTab === 'attendance' && <AttendanceAllowanceTab />}
        {activeTab === 'meal'       && <MealAllowanceTab />}
        {activeTab === 'shift'      && <ShiftAllowanceTab />}
        {activeTab === 'lateness'   && <LatenessTab />}
        {activeTab === 'overtime'   && <OvertimeTab />}
        {activeTab === 'ot-meal'    && <OTMealAllowanceTab />}
      </div>
    </div>
  );
};

export default ReportingPanel;
